import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus, Filter, Users, MessageCircle,
  ChevronLeft, ChevronRight, Heart, Activity,
  Brain, Wind, Zap, Shield, Eye, Ear, Dna, Droplets,
  Baby, Bone, Leaf, Star, Sparkles, UserPlus
} from 'lucide-react';
import { communityService, Post } from '../../services/communityService';
import PostCard from '../../components/community/PostCard';
import PageHeader from '../../components/PageHeader';

const CATEGORY_META: Record<string, { icon: React.ElementType; gradient: string; bg: string; text: string }> = {
  CARDIOVASCULAR:   { icon: Heart,        gradient: 'from-red-500 to-rose-600',      bg: 'bg-red-50',    text: 'text-red-700' },
  METABOLIC:        { icon: Zap,          gradient: 'from-amber-500 to-orange-600',  bg: 'bg-amber-50',  text: 'text-amber-700' },
  RESPIRATORY:      { icon: Wind,         gradient: 'from-sky-500 to-blue-600',      bg: 'bg-sky-50',    text: 'text-sky-700' },
  AUTOIMMUNE:       { icon: Shield,       gradient: 'from-purple-500 to-violet-600', bg: 'bg-purple-50', text: 'text-purple-700' },
  GASTROINTESTINAL: { icon: Activity,     gradient: 'from-green-500 to-emerald-600', bg: 'bg-green-50',  text: 'text-green-700' },
  NEUROLOGICAL:     { icon: Brain,        gradient: 'from-indigo-500 to-blue-700',   bg: 'bg-indigo-50', text: 'text-indigo-700' },
  ENDOCRINE:        { icon: Leaf,         gradient: 'from-teal-500 to-cyan-600',     bg: 'bg-teal-50',   text: 'text-teal-700' },
  MENTAL_HEALTH:    { icon: Sparkles,     gradient: 'from-pink-500 to-rose-500',     bg: 'bg-pink-50',   text: 'text-pink-700' },
  KIDNEY:           { icon: Droplets,     gradient: 'from-blue-500 to-cyan-600',     bg: 'bg-blue-50',   text: 'text-blue-700' },
  REPRODUCTIVE:     { icon: Baby,         gradient: 'from-fuchsia-500 to-pink-600',  bg: 'bg-fuchsia-50',text: 'text-fuchsia-700' },
  MUSCULOSKELETAL:  { icon: Bone,         gradient: 'from-stone-500 to-gray-600',    bg: 'bg-stone-50',  text: 'text-stone-700' },
  DERMATOLOGICAL:   { icon: Star,         gradient: 'from-yellow-500 to-amber-600',  bg: 'bg-yellow-50', text: 'text-yellow-700' },
  HEMATOLOGIC:      { icon: Droplets,     gradient: 'from-red-600 to-rose-700',      bg: 'bg-red-50',    text: 'text-red-800' },
  OCULAR:           { icon: Eye,          gradient: 'from-cyan-500 to-teal-600',     bg: 'bg-cyan-50',   text: 'text-cyan-700' },
  ORL:              { icon: Ear,          gradient: 'from-orange-500 to-amber-600',  bg: 'bg-orange-50', text: 'text-orange-700' },
  RARE_GENETIC:     { icon: Dna,          gradient: 'from-violet-500 to-purple-700', bg: 'bg-violet-50', text: 'text-violet-700' },
};
const DEFAULT_META = { icon: Activity, gradient: 'from-gray-500 to-gray-600', bg: 'bg-gray-50', text: 'text-gray-700' };

const CATEGORY_TRANSLATIONS: Record<string, string> = {
  CARDIOVASCULAR: 'Cardiovasculare', METABOLIC: 'Metabolice', RESPIRATORY: 'Respiratorii',
  AUTOIMMUNE: 'Autoimune', GASTROINTESTINAL: 'Digestive', NEUROLOGICAL: 'Neurologice',
  ENDOCRINE: 'Endocrine', MENTAL_HEALTH: 'Sănătate mintală', KIDNEY: 'Renale',
  REPRODUCTIVE: 'Reproductive', MUSCULOSKELETAL: 'Musculo-scheletice',
  DERMATOLOGICAL: 'Dermatologice', HEMATOLOGIC: 'Hematologice', OCULAR: 'Oculare',
  ORL: 'ORL', RARE_GENETIC: 'Rare / genetice'
};

function MemberAvatar({ member, size = 'md', onClick }: {
  member: { id: string; firstName: string; lastName: string; profilePhotoUrl?: string | null };
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}) {
  const sizeClasses = { sm: 'w-9 h-9 text-xs', md: 'w-12 h-12 text-sm', lg: 'w-14 h-14 text-base' };
  const initials = `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase();
  const colors = [
    'from-blue-400 to-blue-600', 'from-purple-400 to-purple-600',
    'from-green-400 to-green-600', 'from-rose-400 to-rose-600',
    'from-amber-400 to-amber-600', 'from-teal-400 to-teal-600',
    'from-indigo-400 to-indigo-600', 'from-pink-400 to-pink-600',
  ];
  const colorIdx = (member.firstName?.charCodeAt(0) || 0) % colors.length;

  return (
    <button
      onClick={onClick}
      className={`${sizeClasses[size]} rounded-full flex-shrink-0 overflow-hidden ring-2 ring-white shadow-sm hover:scale-105 transition-transform`}
    >
      {member.profilePhotoUrl ? (
        <img src={member.profilePhotoUrl} alt={member.firstName} className="w-full h-full object-cover" />
      ) : (
        <div className={`w-full h-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-white font-bold`}>
          {initials}
        </div>
      )}
    </button>
  );
}

export default function CommunityFeedPage() {
  const { diseaseCode } = useParams<{ diseaseCode: string }>();
  const navigate = useNavigate();

  const [posts, setPosts] = useState<Post[]>([]);
  const [disease, setDisease] = useState<any>(null);
  const [members, setMembers] = useState<Array<{
    id: string; firstName: string; lastName: string;
    userType: string; profilePhotoUrl?: string | null; memberSince: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'unanswered'>('recent');
  const [filterType, setFilterType] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (diseaseCode) loadDiseaseAndPosts();
  }, [diseaseCode, sortBy, filterType, page]);

  useEffect(() => {
    if (diseaseCode) loadMembers();
  }, [diseaseCode]);

  const loadDiseaseAndPosts = async () => {
    try {
      setLoading(true);
      const diseaseData = await communityService.getDiseaseDetails(diseaseCode!);
      setDisease(diseaseData);

      const filters: any = { diseaseCode, sortBy, page, limit: 20 };
      if (filterType !== 'all') filters.type = filterType;

      const postsData = await communityService.getPosts(filters);
      if (page === 1) setPosts(postsData.posts);
      else setPosts(prev => [...prev, ...postsData.posts]);
      setHasMore(postsData.pagination.hasMore);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea postărilor');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      setMembersLoading(true);
      const data = await communityService.getDiseaseMembers(diseaseCode!, 20);
      setMembers(data);
    } catch {
      // silently fail
    } finally {
      setMembersLoading(false);
    }
  };

  const handleDeletePost = (postId: string) => setPosts(posts.filter(p => p.id !== postId));

  const handleReact = async (postId: string, type: string) => {
    try {
      const post = posts.find(p => p.id === postId);
      if (!post) return;
      const userReaction = post.reactions?.[0]?.type;
      if (userReaction === type) {
        await communityService.removeReaction(postId);
        setPosts(posts.map(p => p.id === postId ? { ...p, reactions: [], likesCount: p.likesCount - 1 } : p));
      } else {
        await communityService.reactToPost(postId, type as any);
        setPosts(posts.map(p =>
          p.id === postId
            ? { ...p, reactions: [{ type }], likesCount: userReaction ? p.likesCount : p.likesCount + 1 }
            : p
        ));
      }
    } catch (err) {
      console.error('Error reacting to post:', err);
    }
  };

  const scrollCarousel = (dir: 'left' | 'right') => {
    carouselRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const postTypes = [
    { value: 'all', label: 'Toate', icon: '📝' },
    { value: 'QUESTION', label: 'Întrebări', icon: '❓' },
    { value: 'EXPERIENCE', label: 'Experiențe', icon: '📖' },
    { value: 'VICTORY', label: 'Victorii', icon: '🎉' },
    { value: 'TIP', label: 'Sfaturi', icon: '💡' },
    { value: 'SUPPORT', label: 'Suport', icon: '💚' }
  ];

  const meta = disease ? (CATEGORY_META[disease.category] || DEFAULT_META) : DEFAULT_META;
  const CategoryIcon = meta.icon;

  if (loading && page === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-500 font-medium">Se încarcă comunitatea...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <PageHeader
        title={disease?.name || 'Comunitate'}
        showBack={true}
        backTo="/community"
      />

      {/* ─── HERO ─── */}
      {disease && (
        <div className={`bg-gradient-to-br ${meta.gradient} px-4 pt-5 pb-10 relative overflow-hidden`}>
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-4 w-28 h-28 bg-white/10 rounded-full translate-y-1/2" />

          <div className="relative max-w-5xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 shadow-lg">
                <CategoryIcon className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="inline-block px-2.5 py-0.5 bg-white/20 text-white text-xs font-medium rounded-full mb-1.5">
                  {CATEGORY_TRANSLATIONS[disease.category] || disease.category}
                </span>
                <h1 className="text-xl md:text-2xl font-bold text-white leading-tight">{disease.name}</h1>
                {disease.shortDescription && (
                  <p className="text-white/80 text-xs md:text-sm mt-1 leading-relaxed line-clamp-2 max-w-xl">{disease.shortDescription}</p>
                )}
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5 text-white/90 text-xs">
                    <Users className="w-3.5 h-3.5" />
                    <span className="font-semibold">{disease.memberCount}</span>
                    <span>membri</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/90 text-xs">
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span className="font-semibold">{disease.postCount}</span>
                    <span>postări</span>
                  </div>
                  {disease.postCount > 0 && (
                    <div className="flex items-center gap-1 text-white/90 text-xs">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse"></div>
                      <span>Activă</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── NEW POST BUTTON (overlapping hero) ─── */}
      <div className="max-w-5xl mx-auto px-4">
        <button
          onClick={() => navigate('/community/create-post', { state: { diseaseCode } })}
          className="w-full md:w-auto -mt-5 relative z-10 flex items-center justify-center gap-2 bg-white text-blue-600 font-semibold px-6 py-3.5 rounded-2xl shadow-lg border border-blue-100 hover:bg-blue-50 active:scale-95 transition-all"
        >
          <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
            <Plus className="w-4 h-4 text-white" />
          </div>
          Creează o postare nouă
        </button>
      </div>

      {/* ─── MAIN LAYOUT ─── */}
      <div className="max-w-5xl mx-auto px-4 mt-5">
        <div className="flex flex-col md:flex-row gap-6 items-start">

          {/* ─── POSTS COLUMN ─── */}
          <div className="flex-1 min-w-0 space-y-4">

            {/* MOBILE: Members carousel */}
            {members.length > 0 && (
              <div className="md:hidden bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">Membri ca tine</p>
                      <p className="text-xs text-gray-500">{members.length} persoane</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => scrollCarousel('left')} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                      <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button onClick={() => scrollCarousel('right')} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
                <div ref={carouselRef} className="flex gap-3 overflow-x-auto pb-4 px-4 scrollbar-hide">
                  {membersLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="flex-shrink-0 flex flex-col items-center gap-2 w-16">
                          <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" />
                          <div className="w-12 h-2.5 bg-gray-100 rounded animate-pulse" />
                        </div>
                      ))
                    : members.map(member => (
                        <div key={member.id} className="flex-shrink-0 flex flex-col items-center gap-2 w-16">
                          <MemberAvatar member={member} size="md" onClick={() => navigate(`/community/messages/${member.id}`)} />
                          <p className="text-xs font-medium text-gray-800 leading-tight truncate w-16 text-center">{member.firstName}</p>
                        </div>
                      ))
                  }
                </div>
                <div className="px-4 pb-4 flex items-center justify-between border-t border-gray-50 pt-3">
                  <div className="flex -space-x-2">
                    {members.slice(0, 4).map(m => (
                      <div key={m.id} className="w-6 h-6 rounded-full ring-2 ring-white overflow-hidden">
                        {m.profilePhotoUrl
                          ? <img src={m.profilePhotoUrl} alt={m.firstName} className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center"><span className="text-white text-xs font-bold">{m.firstName?.[0]}</span></div>
                        }
                      </div>
                    ))}
                    {members.length > 4 && <span className="ml-2 text-xs text-gray-500">+{members.length - 4} alții</span>}
                  </div>
                  <button onClick={() => navigate('/community/friends')} className="flex items-center gap-1 text-xs text-blue-600 font-semibold hover:underline">
                    <UserPlus className="w-3.5 h-3.5" />
                    Conectează-te
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full flex items-center justify-between px-4 py-3.5"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">Filtre & Sortare</span>
                  {(sortBy !== 'recent' || filterType !== 'all') && (
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  )}
                </div>
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showFilters ? 'rotate-90' : ''}`} />
              </button>

              {showFilters && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-50">
                  <div className="pt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Sortare</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'recent', label: 'Recente', icon: '🕐' },
                        { value: 'popular', label: 'Populare', icon: '🔥' },
                        { value: 'unanswered', label: 'Fără răspuns', icon: '❓' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setSortBy(opt.value as any); setPage(1); }}
                          className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl text-xs font-medium transition-all ${
                            sortBy === opt.value ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}
                        >
                          <span className="text-base">{opt.icon}</span>
                          <span className="leading-tight text-center">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tip postare</p>
                    <div className="grid grid-cols-3 gap-2">
                      {postTypes.map(type => (
                        <button
                          key={type.value}
                          onClick={() => { setFilterType(type.value); setPage(1); }}
                          className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl text-xs font-medium transition-all ${
                            filterType === type.value ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}
                        >
                          <span className="text-base">{type.icon}</span>
                          <span className="leading-tight text-center">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {!showFilters && (sortBy !== 'recent' || filterType !== 'all') && (
                <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
                  {sortBy !== 'recent' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                      {sortBy === 'popular' ? '🔥 Populare' : '❓ Fără răspuns'}
                      <button onClick={() => setSortBy('recent')} className="ml-0.5 hover:text-blue-900">×</button>
                    </span>
                  )}
                  {filterType !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                      {postTypes.find(t => t.value === filterType)?.icon} {postTypes.find(t => t.value === filterType)?.label}
                      <button onClick={() => setFilterType('all')} className="ml-0.5 hover:text-blue-900">×</button>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Posts list */}
            <div className="space-y-3">
              {posts.map(post => (
                <PostCard key={post.id} post={post} onReact={handleReact} onDelete={handleDeletePost} />
              ))}
            </div>

            {/* Empty state */}
            {posts.length === 0 && !loading && (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-7 h-7 text-gray-400" />
                </div>
                <p className="text-gray-700 font-semibold mb-1">
                  {filterType === 'all' ? 'Nicio postare încă' : 'Nicio postare de acest tip'}
                </p>
                <p className="text-gray-400 text-sm mb-5">Fii primul care împărtășește ceva!</p>
                <button
                  onClick={() => navigate('/community/create-post', { state: { diseaseCode } })}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 active:scale-95 transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Creează prima postare
                </button>
              </div>
            )}

            {/* Load more */}
            {hasMore && (
              <div className="text-center pb-4">
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={loading}
                  className="bg-white text-gray-700 px-8 py-3 rounded-xl border border-gray-200 shadow-sm hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50 font-medium text-sm"
                >
                  {loading
                    ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>Se încarcă...</span>
                    : 'Încarcă mai mult'
                  }
                </button>
              </div>
            )}
          </div>

          {/* ─── DESKTOP SIDEBAR: Members ─── */}
          <aside className="hidden md:block w-72 flex-shrink-0 space-y-4 sticky top-4">
            {/* Members card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 pt-4 pb-3 border-b border-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Membri ca tine</p>
                    <p className="text-xs text-gray-500">{members.length} persoane în comunitate</p>
                  </div>
                </div>
              </div>

              <div className="p-3 space-y-1 max-h-96 overflow-y-auto scrollbar-hide">
                {membersLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-2.5 bg-gray-100 rounded animate-pulse w-24" />
                          <div className="h-2 bg-gray-100 rounded animate-pulse w-16" />
                        </div>
                      </div>
                    ))
                  : members.map(member => (
                      <button
                        key={member.id}
                        onClick={() => navigate(`/community/messages/${member.id}`)}
                        className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-all text-left group"
                      >
                        <MemberAvatar member={member} size="sm" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">
                            {member.firstName} {member.lastName}
                          </p>
                          {member.userType === 'SPECIALIST' ? (
                            <span className="text-xs text-blue-600 font-medium">Specialist</span>
                          ) : (
                            <span className="text-xs text-gray-400">Pacient</span>
                          )}
                        </div>
                        <MessageCircle className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                      </button>
                    ))
                }
              </div>

              <div className="px-4 py-3 border-t border-gray-50">
                <button
                  onClick={() => navigate('/community/friends')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl text-sm font-semibold transition-all"
                >
                  <UserPlus className="w-4 h-4" />
                  Conectează-te cu ei
                </button>
              </div>
            </div>

            {/* Quick stats card */}
            {disease && (
              <div className={`bg-gradient-to-br ${meta.gradient} rounded-2xl p-4 text-white`}>
                <p className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-3">Statistici comunitate</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/80">Membri totali</span>
                    <span className="text-sm font-bold">{disease.memberCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/80">Postări</span>
                    <span className="text-sm font-bold">{disease.postCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/80">Status</span>
                    <span className="flex items-center gap-1 text-sm font-bold">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse"></div>
                      Activă
                    </span>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
